import React, { useState, useCallback } from 'react';
import { View, StyleSheet, FlatList, TouchableOpacity, RefreshControl, Alert } from 'react-native';
import { Text, Card, ActivityIndicator, Divider, FAB, TextInput, Button } from 'react-native-paper';
import { useFocusEffect } from '@react-navigation/native';
import { Ionicons } from '@expo/vector-icons';
import api from '../../services/api';

const LotesScreen = () => {
  const [tab, setTab] = useState('por_vencer');
  const [porVencer, setPorVencer] = useState([]);
  const [vencidos, setVencidos] = useState([]);
  const [resumen, setResumen] = useState({});
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showNuevo, setShowNuevo] = useState(false);
  const [nuevoLote, setNuevoLote] = useState({ producto_id: '', numero_lote: '', fecha_vencimiento: '', cantidad_inicial: '' });

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [pv, vc, res] = await Promise.all([
        api.getLotesPorVencer(60),
        api.getLotesVencidos(),
        api.getLotesResumen(),
      ]);
      setPorVencer(pv);
      setVencidos(vc);
      setResumen(res);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useFocusEffect(useCallback(() => { loadData(); }, [loadData]));

  const diasRestantes = (fecha) => {
    const diff = Math.ceil((new Date(fecha) - new Date()) / (1000 * 60 * 60 * 24));
    return diff;
  };

  const getColorDias = (dias) => {
    if (dias < 0) return '#DC3545';
    if (dias <= 7) return '#FD7E14';
    if (dias <= 30) return '#FFC107';
    return '#28A745';
  };

  const renderResumen = () => (
    <View style={styles.resumenContainer}>
      <View style={[styles.resumenCard, { backgroundColor: '#D4EDDA' }]}>
        <Ionicons name="cube" size={20} color="#155724" />
        <Text style={[styles.resumenNum, { color: '#155724' }]}>{resumen.activos || 0}</Text>
        <Text style={styles.resumenLabel}>Activos</Text>
      </View>
      <View style={[styles.resumenCard, { backgroundColor: '#FFF3CD' }]}>
        <Ionicons name="alert" size={20} color="#856404" />
        <Text style={[styles.resumenNum, { color: '#856404' }]}>{resumen.por_vencer_30d || 0}</Text>
        <Text style={styles.resumenLabel}>Vence 30d</Text>
      </View>
      <View style={[styles.resumenCard, { backgroundColor: '#FFE0CC' }]}>
        <Ionicons name="warning" size={20} color="#FD7E14" />
        <Text style={[styles.resumenNum, { color: '#FD7E14' }]}>{resumen.por_vencer_7d || 0}</Text>
        <Text style={styles.resumenLabel}>Vence 7d</Text>
      </View>
      <View style={[styles.resumenCard, { backgroundColor: '#F8D7DA' }]}>
        <Ionicons name="close-circle" size={20} color="#DC3545" />
        <Text style={[styles.resumenNum, { color: '#DC3545' }]}>{resumen.vencidos || 0}</Text>
        <Text style={styles.resumenLabel}>Vencidos</Text>
      </View>
    </View>
  );

  const renderItem = ({ item }) => {
    const dias = diasRestantes(item.fecha_vencimiento);
    const color = getColorDias(dias);
    return (
      <Card style={[styles.card, { borderLeftWidth: 4, borderLeftColor: color }]}>
        <Card.Content>
          <View style={styles.itemHeader}>
            <View style={{ flex: 1 }}>
              <Text style={styles.productoNombre}>{item.producto_nombre}</Text>
              <Text style={styles.loteNumero}>Lote: {item.numero_lote}</Text>
              {item.marca && <Text style={styles.marca}>{item.marca}</Text>}
            </View>
            <View style={styles.itemRight}>
              <Text style={[styles.diasText, { color }]}>
                {dias < 0 ? `Venció hace ${Math.abs(dias)}d` : dias === 0 ? 'Vence HOY' : `${dias} días`}
              </Text>
              <Text style={styles.cantidad}>Cant: {parseFloat(item.cantidad_actual)}</Text>
            </View>
          </View>
          <Divider style={{ marginVertical: 6 }} />
          <View style={styles.itemFooter}>
            <Text style={styles.fechaText}>Vence: {new Date(item.fecha_vencimiento).toLocaleDateString('es-HN')}</Text>
            {item.codigo && <Text style={styles.codigoText}>{item.producto_codigo}</Text>}
          </View>
        </Card.Content>
      </Card>
    );
  };

  if (loading && !refreshing) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color="#FD7E14" />
        <Text style={{ marginTop: 10, color: '#666' }}>Cargando lotes...</Text>
      </View>
    );
  }

  const dataToShow = tab === 'por_vencer' ? porVencer : vencidos;

  return (
    <View style={styles.container}>
      {renderResumen()}

      <View style={styles.tabsContainer}>
        <TouchableOpacity style={[styles.tab, tab === 'por_vencer' && styles.tabActive]} onPress={() => setTab('por_vencer')}>
          <Ionicons name="alert" size={16} color={tab === 'por_vencer' ? '#FFC107' : '#999'} />
          <Text style={[styles.tabText, tab === 'por_vencer' && styles.tabTextActive]}>Por Vencer ({porVencer.length})</Text>
        </TouchableOpacity>
        <TouchableOpacity style={[styles.tab, tab === 'vencidos' && styles.tabActive]} onPress={() => setTab('vencidos')}>
          <Ionicons name="close-circle" size={16} color={tab === 'vencidos' ? '#DC3545' : '#999'} />
          <Text style={[styles.tabText, tab === 'vencidos' && styles.tabTextActive]}>Vencidos ({vencidos.length})</Text>
        </TouchableOpacity>
      </View>

      <FlatList
        data={dataToShow}
        keyExtractor={item => item.id.toString()}
        renderItem={renderItem}
        contentContainerStyle={styles.listContainer}
        ItemSeparatorComponent={() => <View style={{ height: 8 }} />}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={() => { setRefreshing(true); loadData(); }} />}
        ListEmptyComponent={
          <View style={styles.emptyContainer}>
            <Ionicons name={tab === 'por_vencer' ? 'checkmark-circle-outline' : 'happy-outline'} size={64} color="#ccc" />
            <Text style={styles.emptyText}>{tab === 'por_vencer' ? 'No hay lotes por vencer' : 'No hay lotes vencidos'}</Text>
          </View>
        }
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#f5f5f5' },
  loadingContainer: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  resumenContainer: { flexDirection: 'row', padding: 8, justifyContent: 'space-between' },
  resumenCard: { flex: 1, alignItems: 'center', padding: 10, borderRadius: 8, marginHorizontal: 3, elevation: 1 },
  resumenNum: { fontSize: 20, fontWeight: 'bold', marginTop: 2 },
  resumenLabel: { fontSize: 9, color: '#666', marginTop: 2, textAlign: 'center' },
  tabsContainer: { flexDirection: 'row', paddingHorizontal: 8, marginBottom: 8 },
  tab: { flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', paddingVertical: 10, borderBottomWidth: 2, borderBottomColor: 'transparent' },
  tabActive: { borderBottomColor: '#FD7E14' },
  tabText: { fontSize: 13, color: '#999', marginLeft: 4 },
  tabTextActive: { color: '#333', fontWeight: 'bold' },
  listContainer: { padding: 8 },
  card: { elevation: 2 },
  itemHeader: { flexDirection: 'row', justifyContent: 'space-between' },
  productoNombre: { fontSize: 15, fontWeight: 'bold', color: '#333' },
  loteNumero: { fontSize: 12, color: '#666', marginTop: 2 },
  marca: { fontSize: 12, color: '#17A2B8', marginTop: 2 },
  itemRight: { alignItems: 'flex-end' },
  diasText: { fontSize: 14, fontWeight: 'bold' },
  cantidad: { fontSize: 12, color: '#666', marginTop: 2 },
  itemFooter: { flexDirection: 'row', justifyContent: 'space-between' },
  fechaText: { fontSize: 12, color: '#888' },
  codigoText: { fontSize: 12, color: '#999' },
  emptyContainer: { justifyContent: 'center', alignItems: 'center', paddingVertical: 50 },
  emptyText: { marginTop: 10, color: '#888', fontSize: 16 },
});

export default LotesScreen;
